const SHARED_POI_FIELDS = [
  'category',
  'tier',
  'photoUrl',
  'sphericalPhotoUrl',
  'coordinates',
  'orientation',
  'openingHours',
  'useSSML',
  'relatedGuide',
  'signLanguageVideoUrl',
  'flexibleContent'
];

const TRANSLATABLE_METADATA_PATHS = [
  'name',
  'description',
  'subtitle',
  'categories',
  'tags',
  'theme',
  'targetAudience',
  'openingHours',
  'opening_hours',
  'accessibility',
  'feedback',
  'feedback.micro_prompts',
  'feedback.issue_question',
  'translationModel',
  'museum_info.description',
  'museum_info.venue_type',
  'museum_info.accessibility',
  'museum_info.admission',
  'museum_info.cafe',
  'museum_info.giftShop',
  'museum_info.gift_shop',
  'museum_info.openingHours',
  'museum_info.opening_hours',
  'museum_info.wifi',
  'museum_info.categories',
  'museum_info.tags',
  'museum_info.theme',
  'museum_info.targetAudience'
];

const NON_SHARED_METADATA_FIELDS = [
  'id',
  'language',
  'originalLanguage',
  'filename',
  'createdAt',
  'updatedAt',
  'translatedAt',
  'translated_at',
  'translated_itinerary',
  'lastAudioGeneration',
  'translationSourceGuideId',
  'translationSourceLanguage',
  'translationSourceSignature',
  'translationSourceSignatureUpdatedAt',
  'translation_source_signature'
];

const NON_SHARED_METADATA_PATHS = new Set([
  'id',
  'language',
  'originalLanguage',
  'filename',
  'createdAt',
  'updatedAt',
  'translatedAt',
  'translated_at',
  'translated_itinerary',
  'lastAudioGeneration',
  'translationSourceGuideId',
  'translationSourceLanguage',
  'translationSourceSignature',
  'translationSourceSignatureUpdatedAt',
  'translation_source_signature'
]);

export const SHARED_FIELD_LABELS = {
  category: 'Category',
  tier: 'Tier',
  photoUrl: 'Photo URL',
  sphericalPhotoUrl: '360 Photo URL',
  coordinates: 'Coordinates',
  estimatedDuration: 'Estimated Duration',
  openingHours: 'Opening Hours',
  keywords: 'Keywords',
  useSSML: 'Use SSML',
  relatedGuide: 'Related Guide',
  signLanguageVideoUrl: 'Sign Language Video URL',
  flexibleContent: 'Flexible Content',
  thumbnailUrl: 'Thumbnail Image',
  b2bClient: 'B2B Client Guide',
  has_qr_codes: 'Has QR Codes',
  orientation: 'Orientation Guidance',
  web_based_guide: 'Web-Based Guide',
  guide_type: 'Guide Type',
  download_on_location: 'Download on Location',
  download_radius_meters: 'Download Radius',
  download_priority: 'Download Priority',
  ai_radius_limitation: 'AI Radius Limitation',
  ai_radius_meters: 'AI Access Radius',
  auto_play_radius_meters: 'Auto-play Radius',
  '360visit': '360 Visit Available',
  '360visitJSON': '360 Visit JSON URL',
  '360visitUrl': '360 Visit URL',
  'museum_info.logoUrl': 'Business Logo URL'
};

const TRANSLATABLE_POI_FIELDS = [
  'name',
  'description',
  'inDepthDescription',
  'historicalContext',
  'culturalSignificance',
  'hiddenGems',
  'subtitle',
  'tips',
  'orientationInstructions'
];

const SAME_AS_SOURCE_ALLOWED_METADATA_PATHS = new Set([
  'name'
]);

const SAME_AS_SOURCE_ALLOWED_POI_FIELDS = new Set([
  'name'
]);

const deepClone = (value) => JSON.parse(JSON.stringify(value));

const valuesEqual = (left, right) => JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
const normalizeText = (value) => String(value || '').trim().replace(/\s+/g, ' ');
const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const isTranslatableMetadataPath = (path) => TRANSLATABLE_METADATA_PATHS.some((candidate) => (
  path === candidate || path.startsWith(`${candidate}.`)
));

const shouldExcludeMetadataPath = (path) => {
  if (!path) return false;

  if (NON_SHARED_METADATA_PATHS.has(path)) {
    return true;
  }

  if (isTranslatableMetadataPath(path)) {
    return true;
  }

  return path.split('.').some((segment) => NON_SHARED_METADATA_FIELDS.includes(segment));
};

const collectComparableMetadataPaths = (sourceValue, targetValue, parentPath = '') => {
  const sourceIsObject = isPlainObject(sourceValue);
  const targetIsObject = isPlainObject(targetValue);

  if (!sourceIsObject && !targetIsObject) {
    return parentPath && !shouldExcludeMetadataPath(parentPath) ? [parentPath] : [];
  }

  const keys = new Set([
    ...Object.keys(sourceIsObject ? sourceValue : {}),
    ...Object.keys(targetIsObject ? targetValue : {})
  ]);

  const paths = [];
  keys.forEach((key) => {
    const path = parentPath ? `${parentPath}.${key}` : key;
    if (shouldExcludeMetadataPath(path)) {
      return;
    }

    const nestedPaths = collectComparableMetadataPaths(
      sourceIsObject ? sourceValue?.[key] : undefined,
      targetIsObject ? targetValue?.[key] : undefined,
      path
    );

    if (nestedPaths.length > 0) {
      paths.push(...nestedPaths);
      return;
    }

    paths.push(path);
  });

  return paths;
};

const getValueAtPath = (source, path) => {
  if (!path) return source;

  return path.split('.').reduce((current, segment) => (
    current === null || current === undefined ? undefined : current[segment]
  ), source);
};

const normalizeGuideMetadataForComparison = (metadata = {}) => {
  const normalized = deepClone(metadata || {});

  if (!normalized.download_on_location) {
    const legacyForceDownload = normalized.force_download_on_location;
    if (legacyForceDownload === true || legacyForceDownload === 'true') {
      normalized.download_on_location = 'force';
    }
  }

  if (normalized.download_on_location === 'off') {
    delete normalized.download_on_location;
  }

  if (normalized.download_on_location) {
    if (normalized.download_radius_meters === undefined || normalized.download_radius_meters === null || normalized.download_radius_meters === '') {
      normalized.download_radius_meters = 5000;
    }

    if (!normalized.download_priority) {
      normalized.download_priority = 'normal';
    }
  }

  if (normalized.b2bClient === undefined && normalized.b2b_client !== undefined) {
    normalized.b2bClient = Boolean(normalized.b2b_client);
  }

  delete normalized.force_download_on_location;
  delete normalized.b2b_client;

  return normalized;
};

const getComparableGuideMetadata = (guide) => normalizeGuideMetadataForComparison(guide?.metadata || {});

export const getComparableMetadataValue = (guide, path) => getValueAtPath(getComparableGuideMetadata(guide), path);

const setValueAtPath = (target, path, value) => {
  const segments = path.split('.');
  let current = target;

  segments.forEach((segment, index) => {
    const isLeaf = index === segments.length - 1;

    if (isLeaf) {
      if (value === undefined) {
        if (current && typeof current === 'object') {
          delete current[segment];
        }
        return;
      }

      current[segment] = deepClone(value);
      return;
    }

    const nextValue = current?.[segment];
    if (!isPlainObject(nextValue)) {
      current[segment] = {};
    }
    current = current[segment];
  });
};

const cleanupEmptyParents = (target, path) => {
  const segments = path.split('.');

  for (let index = segments.length - 1; index > 0; index -= 1) {
    const parentPath = segments.slice(0, index).join('.');
    const parentValue = getValueAtPath(target, parentPath);
    if (!isPlainObject(parentValue) || Object.keys(parentValue).length > 0) {
      break;
    }

    const grandParentPath = segments.slice(0, index - 1).join('.');
    const grandParent = grandParentPath ? getValueAtPath(target, grandParentPath) : target;
    if (grandParent && typeof grandParent === 'object') {
      delete grandParent[segments[index - 1]];
    }
  }
};

const getSharedMetadataPaths = (sourceGuide, targetGuide) => (
  Array.from(new Set(
    collectComparableMetadataPaths(getComparableGuideMetadata(sourceGuide), getComparableGuideMetadata(targetGuide))
  )).sort()
);

export const getTranslationGroupId = (guide) => {
  const metadata = guide?.metadata || guide || {};
  return String(metadata.parent_guide_id || metadata.id || '').trim();
};

export const getLanguageCode = (guide) => {
  const metadata = guide?.metadata || guide || {};
  return String(metadata.language || '').trim().toLowerCase();
};

export const getPointSequence = (point, index) => {
  const pointId = String(point?.id || '');
  const match = pointId.match(/^[^_]+_(\d+)_/);
  if (match?.[1]) {
    return Number(match[1]);
  }
  return index + 1;
};

export const buildTargetPointId = (guideId, language, sequence) => {
  const shortLanguage = String(language || 'en').toLowerCase().split(/[-_]/)[0] || 'en';
  return `${guideId}_${sequence}_${shortLanguage}`;
};

export const buildGuideTranslationSourceSignature = (guide) => {
  const metadata = guide?.metadata || {};
  const points = Array.isArray(guide?.points) ? guide.points : [];

  const signaturePayload = {
    metadata: TRANSLATABLE_METADATA_PATHS.reduce((acc, fieldPath) => {
      acc[fieldPath] = deepClone(getValueAtPath(metadata, fieldPath) ?? null);
      return acc;
    }, {}),
    points: points.map((point, index) => ({
      sequence: getPointSequence(point, index),
      fields: TRANSLATABLE_POI_FIELDS.reduce((acc, field) => {
        acc[field] = normalizeText(point?.[field]);
        return acc;
      }, {})
    }))
  };

  return JSON.stringify(signaturePayload);
};

export const buildTranslationStatePayload = (sourceGuide, targetGuide) => ({
  translationGuideId: targetGuide?.metadata?.id || '',
  translationLanguage: targetGuide?.metadata?.language || '',
  sourceGuideId: sourceGuide?.metadata?.id || getTranslationGroupId(sourceGuide),
  sourceLanguage: sourceGuide?.metadata?.language || '',
  reviewedAt: new Date().toISOString(),
  translationSourceSignature: buildGuideTranslationSourceSignature(sourceGuide)
});

export const compareGuideTranslationContent = (sourceGuide, targetGuide, translationState = null) => {
  const sourceLanguage = getLanguageCode(sourceGuide);
  const targetLanguage = getLanguageCode(targetGuide);
  const sourcePoints = Array.isArray(sourceGuide?.points) ? sourceGuide.points : [];
  const targetPoints = Array.isArray(targetGuide?.points) ? targetGuide.points : [];
  const currentSourceSignature = buildGuideTranslationSourceSignature(sourceGuide);
  const storedSourceSignature = String(
    translationState?.translationSourceSignature
      || translationState?.translation_source_signature
      || ''
  ).trim() || String(
    targetGuide?.metadata?.translationSourceSignature
      || targetGuide?.metadata?.translation_source_signature
      || ''
  ).trim();

  const metadataMissing = [];
  const metadataSameAsSource = [];
  const missingFieldDetails = [];
  const sameAsSourceDetails = [];

  TRANSLATABLE_METADATA_PATHS.forEach((fieldPath) => {
    const sourceValue = getValueAtPath(sourceGuide?.metadata, fieldPath);
    const sourceHasValue = Array.isArray(sourceValue)
      ? sourceValue.length > 0
      : isPlainObject(sourceValue)
        ? Object.keys(sourceValue).length > 0
        : normalizeText(sourceValue) !== '';

    if (!sourceHasValue) return;

    const targetValue = getValueAtPath(targetGuide?.metadata, fieldPath);
    const targetHasValue = Array.isArray(targetValue)
      ? targetValue.length > 0
      : isPlainObject(targetValue)
        ? Object.keys(targetValue).length > 0
        : normalizeText(targetValue) !== '';

    if (!targetHasValue) {
      metadataMissing.push(fieldPath);
      missingFieldDetails.push({
        type: 'metadata',
        field: fieldPath
      });
      return;
    }

    if (
      sourceLanguage !== targetLanguage
      && !SAME_AS_SOURCE_ALLOWED_METADATA_PATHS.has(fieldPath)
      && valuesEqual(sourceValue, targetValue)
    ) {
      metadataSameAsSource.push(fieldPath);
      sameAsSourceDetails.push({
        type: 'metadata',
        field: fieldPath
      });
    }
  });

  const pointDiffs = [];
  let missingPointCount = 0;
  let missingFieldCount = 0;
  let sameAsSourceCount = 0;

  sourcePoints.forEach((sourcePoint, index) => {
    const sequence = getPointSequence(sourcePoint, index);
    const targetPoint = targetPoints.find((point, targetIndex) => getPointSequence(point, targetIndex) === sequence)
      || targetPoints[index]
      || null;

    if (!targetPoint) {
      missingPointCount += 1;
      pointDiffs.push({
        sequence,
        sourceName: sourcePoint?.name || `POI ${sequence}`,
        missingPoint: true,
        missingFields: [],
        sameAsSourceFields: []
      });
      return;
    }

    const missingFields = [];
    const sameAsSourceFields = [];

    TRANSLATABLE_POI_FIELDS.forEach((field) => {
      const sourceValue = normalizeText(sourcePoint?.[field]);
      if (!sourceValue) return;

      const targetValue = normalizeText(targetPoint?.[field]);
      if (!targetValue) {
        missingFields.push(field);
        missingFieldDetails.push({
          type: 'poi',
          sequence,
          sourceName: sourcePoint?.name || `POI ${sequence}`,
          field
        });
        return;
      }

      if (
        sourceLanguage !== targetLanguage
        && !SAME_AS_SOURCE_ALLOWED_POI_FIELDS.has(field)
        && targetValue === sourceValue
      ) {
        sameAsSourceFields.push(field);
        sameAsSourceDetails.push({
          type: 'poi',
          sequence,
          sourceName: sourcePoint?.name || `POI ${sequence}`,
          field
        });
      }
    });

    missingFieldCount += missingFields.length;
    sameAsSourceCount += sameAsSourceFields.length;

    if (missingFields.length > 0 || sameAsSourceFields.length > 0) {
      pointDiffs.push({
        sequence,
        sourceName: sourcePoint?.name || `POI ${sequence}`,
        missingPoint: false,
        missingFields,
        sameAsSourceFields
      });
    }
  });

  const sourceChangedSinceReview = Boolean(storedSourceSignature) && storedSourceSignature !== currentSourceSignature;
  const reviewBaselineMissing = !storedSourceSignature;
  const needsReview = reviewBaselineMissing
    || sourceChangedSinceReview
    || missingPointCount > 0
    || missingFieldCount > 0
    || sameAsSourceCount > 0;

  return {
    reviewBaselineMissing,
    sourceChangedSinceReview,
    needsReview,
    storedSourceSignature,
    currentSourceSignature,
    metadataMissing,
    metadataSameAsSource,
    pointDiffs,
    missingPointCount,
    missingFieldCount,
    sameAsSourceCount,
    missingFieldDetails,
    sameAsSourceDetails,
    totalIssues: metadataMissing.length + metadataSameAsSource.length + missingPointCount + missingFieldCount + sameAsSourceCount
  };
};

export const compareGuideSharedContent = (sourceGuide, targetGuide) => {
  const metadataDiffs = [];
  const sourceMetadata = getComparableGuideMetadata(sourceGuide);
  const targetMetadata = getComparableGuideMetadata(targetGuide);

  getSharedMetadataPaths(sourceGuide, targetGuide).forEach((path) => {
    if (!valuesEqual(getValueAtPath(sourceMetadata, path), getValueAtPath(targetMetadata, path))) {
      metadataDiffs.push(path);
    }
  });

  const pointDiffs = [];
  const targetPoints = Array.isArray(targetGuide?.points) ? targetGuide.points : [];

  (sourceGuide?.points || []).forEach((sourcePoint, index) => {
    const sequence = getPointSequence(sourcePoint, index);
    const targetPoint = targetPoints.find((point, targetIndex) => getPointSequence(point, targetIndex) === sequence)
      || targetPoints[index]
      || null;

    const fieldDiffs = [];
    SHARED_POI_FIELDS.forEach((field) => {
      if (!valuesEqual(sourcePoint?.[field], targetPoint?.[field])) {
        fieldDiffs.push(field);
      }
    });

    if (fieldDiffs.length > 0 || !targetPoint) {
      pointDiffs.push({
        sequence,
        sourceName: sourcePoint?.name || `POI ${sequence}`,
        targetName: targetPoint?.name || '',
        missing: !targetPoint,
        fields: fieldDiffs
      });
    }
  });

  return {
    metadataDiffs,
    pointDiffs,
    totalDiffs: metadataDiffs.length + pointDiffs.reduce((sum, item) => sum + (item.fields.length || 1), 0)
  };
};

export const syncSharedFieldsToGuide = (sourceGuide, targetGuide) => {
  const nextGuide = deepClone(targetGuide || {});
  nextGuide.metadata = deepClone(targetGuide?.metadata || {});
  const sourceMetadata = getComparableGuideMetadata(sourceGuide);

  getSharedMetadataPaths(sourceGuide, targetGuide).forEach((path) => {
    const sourceValue = getValueAtPath(sourceMetadata, path);
    setValueAtPath(nextGuide.metadata, path, sourceValue);

    if (sourceValue === undefined) {
      cleanupEmptyParents(nextGuide.metadata, path);
    }
  });

  delete nextGuide.metadata.force_download_on_location;
  delete nextGuide.metadata.b2b_client;

  const sourcePoints = Array.isArray(sourceGuide?.points) ? sourceGuide.points : [];
  const targetPoints = Array.isArray(targetGuide?.points) ? deepClone(targetGuide.points) : [];

  nextGuide.points = sourcePoints.map((sourcePoint, index) => {
    const sequence = getPointSequence(sourcePoint, index);
    const existingTargetPoint = targetPoints.find((point, targetIndex) => getPointSequence(point, targetIndex) === sequence)
      || targetPoints[index]
      || null;

    const nextPoint = existingTargetPoint ? deepClone(existingTargetPoint) : {};

    Object.keys(sourcePoint || {}).forEach((field) => {
      if (!TRANSLATABLE_POI_FIELDS.includes(field) && !SHARED_POI_FIELDS.includes(field) && field !== 'id') {
        if (nextPoint[field] === undefined) {
          nextPoint[field] = deepClone(sourcePoint[field]);
        }
      }
    });

    SHARED_POI_FIELDS.forEach((field) => {
      if (sourcePoint?.[field] === undefined) {
        delete nextPoint[field];
        return;
      }
      nextPoint[field] = deepClone(sourcePoint[field]);
    });

    nextPoint.id = existingTargetPoint?.id || buildTargetPointId(
      getTranslationGroupId(targetGuide) || getTranslationGroupId(sourceGuide),
      targetGuide?.metadata?.language || sourceGuide?.metadata?.language,
      sequence
    );

    return nextPoint;
  });

  return nextGuide;
};
